import { ReactNode, useEffect, useMemo, useState } from 'react';

import GrfqQuotesStore from '@paradigm/features/src/unified-rfqs/store/grfq-quotes';
import { RfqId } from '#/unified-rfqs/entities/rfq';
import createSafeContext from '@paradigm/utils/createSafeContext';
import { GrfqQuote, GrfqQuotes } from '#/unified-rfqs/entities/grfq/domain';
import { createGrfqService, GrfqService } from '#/unified-rfqs/services/grfq';
import {
  CallbackHandlers,
  WebSocketStatus,
} from '#/unified-rfqs/services/grfq-ws';
import { useNotificationHandlerActions } from '#/unified-rfqs/ui/notifications/notifications-handler-context';
import { Trade, TradeTapeItem } from '#/unified-rfqs/entities/shared';
import { useOrder } from '#/unified-rfqs/contexts/unified-orders';
import { useTrade } from '#/unified-rfqs/contexts/unified-trades';
import { useTradeTapeItem } from '#/unified-rfqs/contexts/unified-trade-tapes';
import RfqsStore from '#/unified-rfqs/store/rfqs';
import { GrfqBboServiceContextProvider } from '#/unified-rfqs/contexts/grfq-bbo';
import OrdersStore from '#/unified-rfqs/store/orders';
import TradesStore from '#/unified-rfqs/store/trades';
import TradeTapeStore from '../store/trade-tape';

export const [useGrfqService, GrfqServiceCtxProvider] =
  createSafeContext<GrfqService>('GrfqService');

export const [useGrfqWebSocket, GrfqWsProvider] =
  createSafeContext<WebSocketStatus>('GrfqWsService');

type BaseCallbackHandlers = Omit<
  CallbackHandlers,
  'handleRfqReceived' | 'handleRfqUpdated' | 'storeUserTrade'
>;

interface Props {
  readonly children: ReactNode;
  readonly handlers: BaseCallbackHandlers;
  readonly rfqStore: RfqsStore;
  readonly grfqQuoteStore: GrfqQuotesStore;
  readonly ordersStore: OrdersStore;
  readonly tradesStore: TradesStore;
  readonly tradeTapeStore: TradeTapeStore;
}

export function GrfqServiceProvider({
  children,
  handlers,
  rfqStore,
  grfqQuoteStore,
  ordersStore,
  tradesStore,
  tradeTapeStore,
}: Props) {
  const { handleRfqReceived, handleRfqUpdated, storeUserTrade } =
    useNotificationHandlerActions();
  const grfqService = useMemo(
    () =>
      createGrfqService({
        handlers: {
          ...handlers,
          handleRfqReceived,
          handleRfqUpdated,
          storeUserTrade,
        },
        rfqStore,
        grfqQuoteStore,
        ordersStore,
        tradesStore,
        tradeTapeStore,
      }),
    [
      handlers,
      handleRfqReceived,
      handleRfqUpdated,
      storeUserTrade,
      rfqStore,
      grfqQuoteStore,
      ordersStore,
      tradesStore,
      tradeTapeStore,
    ],
  );

  const { status } = useGrfqServiceWs(grfqService);

  useEffect(() => {
    return () => {
      grfqService.teardown();
    };
  }, [grfqService]);

  return (
    <GrfqServiceCtxProvider value={grfqService}>
      <GrfqWsProvider value={status}>
        <GrfqBboServiceContextProvider>
          {children}
        </GrfqBboServiceContextProvider>
      </GrfqWsProvider>
    </GrfqServiceCtxProvider>
  );
}

export function useGrfqServiceWs(grfqService: GrfqService) {
  const [status, setStatus] = useState<WebSocketStatus>({
    error: '',
    connected: false,
    subscribed: false,
    subscribing: false,
  });

  useEffect(() => {
    const unsubscribeHandlers = grfqService.setup();
    const unsubscribeStatus = grfqService.subscribeWebSocketStatus(
      (partialStatus) =>
        setStatus((curStatus) => ({
          ...curStatus,
          ...partialStatus,
        })),
    );

    return () => {
      unsubscribeHandlers();
      unsubscribeStatus();
      grfqService.teardown();
    };
  }, [grfqService]);

  return { status };
}

export function useGrfqRfqQuotes(rfqId: RfqId) {
  const grfqService = useGrfqService();
  const [quotes, setQuotes] = useState<GrfqQuotes>(
    grfqService.selectRfqQuotes(rfqId),
  );

  useEffect(
    () => grfqService.subscribeRfqQuotes(rfqId, setQuotes),
    [rfqId, grfqService],
  );

  return quotes;
}

export function useGrfqMyQuote(quoteId: number) {
  const quote = useOrder(quoteId);

  if (quote.rfqType !== 'grfq') {
    throw new TypeError(`Order ${quote.id} is not of kind grfq`);
  }

  return quote;
}

export function useGrfqTrade(tradeId: Trade['id']) {
  const trade = useTrade(tradeId);

  if (trade.rfqType !== 'grfq') {
    throw new TypeError(`Trade ${tradeId} is not of kind grfq`);
  }

  return trade;
}

export function useGrfqTradeTapeItem(tradeId: TradeTapeItem['id']) {
  const trade = useTradeTapeItem(tradeId);

  if (trade.rfqType !== 'grfq') {
    throw new TypeError(`Trade tape item ${tradeId} is not of kind grfq`);
  }

  return trade;
}

export interface AggGrfqQuote extends GrfqQuote {
  numQuotes: number;
}

export const [useMyAggBids, MyAggBidsProvider] =
  createSafeContext<AggGrfqQuote[]>('MyAggBids');
export const [useMyAggAsks, MyAggAsksProvider] =
  createSafeContext<AggGrfqQuote[]>('MyAggAsks');

export const [useAggBid, AggBidProvider] = createSafeContext<
  AggGrfqQuote | undefined
>('AggBid');
export const [useAggAsk, AggAskProvider] = createSafeContext<
  AggGrfqQuote | undefined
>('AggAsk');
